/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ActivatedRoute } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderNavigationService,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import { validateSiteAddressInfo } from '@gh/builder-shared-data';
import { createDefaultGridModel } from '@gh/common-internal-shared';
import {
  ActionFlow,
  AppRouter,
  fieldStateByEntityConfig,
  getRouteInitData,
  IS_REGION_AU_OR_NZ,
} from '@gh/common-shared';
import { MessageBasket } from '@gh/core-messages';
import { TranslationService } from '@gh/core-mls';
import { FormMode } from '@gh/core-ui';
import {
  buildEditableSelectColumn,
  EditableCellOptionSet,
  GridDefinition,
  GridDefinitionFactory,
} from '@gh/core-ui-grid';

import { assign, constant } from 'lodash';

import { RfqGeneral } from '../../resources/lead.model';
import { getInspectionItemId, hasInspectionAvailableValues, InspectionItem, RfqSite } from '../../resources/rfq.model';
import { SiteDetailsBusinessService } from '../../resources/site-details-business.service';
import { RfqSiteDetailsFormState } from './rfq-site-details-form.service';

@Component({
  selector: 'gh-lead-details-rfq-site-details',
  template: require('./rfq-site-details.component.html'),
  styles: [require('./rfq-site-details.component.scss')],
  viewProviders: [
    RfqSiteDetailsFormState,
  ],
})

export class RfqSiteDetailsComponent extends AbstractFormComponent<RfqSite> implements OnInit {
  generalInfo: RfqGeneral;
  siteDetails: RfqSite;
  gridDefinition: GridDefinition;

  constructor(private route: ActivatedRoute,
              private translations: TranslationService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private router: AppRouter,
              public form: RfqSiteDetailsFormState,
              private navigationService: BuilderNavigationService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private businessService: SiteDetailsBusinessService) {
    super();
  }

  get isAUorNZ(): boolean {
    return IS_REGION_AU_OR_NZ;
  }

  get title(): string {
    return {
      [FormMode.CreateMode]: 'lbl_site_create',
      [FormMode.EditMode]: 'lbl_site_edit',
      [FormMode.ViewMode]: 'lbl_site_view',
    }[this.form.mode];
  }

  ngOnInit(): void {
    const { siteDetails, salesFolderId, generalInfo } = getRouteInitData(this.route);

    assign(this, { siteDetails, salesFolderId, generalInfo });
    this.form.build(siteDetails, generalInfo);

    this.buildGridDefinition();
  }

  onCancel(): void {
    this.router.navigateBack();
  }

  goToAel(): void {
    this.navigationService.goToAel({
      label: 'lbl_sites',
      entityType: 'SITE',
      entityId: this.siteDetails.id,
    });
  }

  onInspectedChanged($event: MatCheckboxChange): void {
    this.form.updateInspectedDate($event.checked);
  }

  get completeFlow(): ActionFlow<FormFlowState<RfqSite>> {
    return this.pipelines.complete({
      validate: this.commonActions.validate(
        (state: FormFlowState<RfqSite>) => this.validateBeforeProcess(state.value),
      ),
      execute: this.commonActions.execute((state: FormFlowState<RfqSite>) =>
        this.businessService.save(this.generalInfo.salesFolderId, state.form.submitValue)),
      finish: (state: FormFlowState<RfqSite>) => {
        const { generalInfo } = getRouteInitData(this.route);
        const { value } = state;

        if (this.form.createMode) {
          this.router.switchTo(['/sales/sales-folders/', this.generalInfo.salesFolderId, 'site', value.id]);
        } else {
          this.form.build(value, generalInfo);
        }
      },
    });
  }

  private validateBeforeProcess(site: RfqSite): Maybe<MessageBasket> {
    const errorId = validateSiteAddressInfo(site);
    return errorId ? MessageBasket.error(errorId) : void 0;
  }

  private buildGridDefinition(): void {
    const model = createDefaultGridModel<InspectionItem>({
      editable: this.form.editable,
      getId: getInspectionItemId,
      getCellDef: fieldStateByEntityConfig({ value: {} }),
      command: (entity, _, value) => this.form.setInspectionValue(getInspectionItemId(entity), value),
    });

    this.gridDefinition = this.gridDefinitionFactory.buildManualWithModel({
      model,
      columns: [{
        headerName: this.translations.get('lbl_title'),
        field: 'name',
        width: 200,
      }, {
        headerName: this.translations.get('lbl_selection'),
        field: 'value',
        width: 200,
        suppressSizeToFit: true,
        ...buildEditableSelectColumn(this.buildSelectionOptionSet(), model.getCellAccessor('value'), {
          hasAlternativeText: ({ data }) => !hasInspectionAvailableValues(data),
          getAlternativeText: constant(this.translations.get('lbl_no_available_values')),
        }),
      }],
    });
  }

  private buildSelectionOptionSet(): EditableCellOptionSet {
    return {
      format: (_, value) => value,
      getOptions: ({ data }: { data: InspectionItem }) =>
        data.availableValues.map((value) => ({ id: value, name: value })),
      hasOptions: ({ data }) => hasInspectionAvailableValues(data),
    };
  }
}
